import { flexRender, type RowData } from '@tanstack/react-table';
import * as React from 'react';

import { DimeCollapsible, DimeCollapsibleContent } from '../DimeCollapsible.js';
import { DimeTableCell, DimeTableRow } from '../DimeTable/index.js';
import { readOwnFlag } from '../table-internals/read-own-flag.js';
import type { DimeNestedTableProps, ParentRow } from './DimeNestedTable.types.js';
import { readCachedChildren } from './read-cached-children.js';

interface NestedTableRowProps<TParent extends RowData, TChild> {
  row: ParentRow<TParent>;
  rowIndex: number;
  rowCount: number;
  /** When true, `rows` is one page of a larger set, so neither position nor total can be stated. */
  isServerPaginated: boolean;
  visibleColumnCount: number;
  childRowsByParentId: Record<string, TChild[] | undefined>;
  loadingParentIds?: Record<string, boolean>;
  renderChildRows: DimeNestedTableProps<TParent, TChild>['renderChildRows'];
  cellClassName: (columnId: string) => string | undefined;
  isActiveRow: boolean;
  registerRowElement: (parentId: string, element: HTMLTableRowElement | null) => void;
  onRowFocus: (parentId: string) => void;
  onRowBlur: (event: React.FocusEvent) => void;
  onRowKeyDown: (event: React.KeyboardEvent, row: ParentRow<TParent>) => void;
}

/** One parent row plus, when open, the collapsible panel holding its children. */
export function NestedTableRow<TParent extends RowData, TChild>({
  row,
  rowIndex,
  rowCount,
  isServerPaginated,
  visibleColumnCount,
  childRowsByParentId,
  loadingParentIds,
  renderChildRows,
  cellClassName,
  isActiveRow,
  registerRowElement,
  onRowFocus,
  onRowBlur,
  onRowKeyDown,
}: Readonly<NestedTableRowProps<TParent, TChild>>) {
  const parentId = row.id;
  const canExpand = row.getCanExpand();
  const isExpanded = row.getIsExpanded();

  return (
    <>
      <DimeTableRow
        ref={(element: HTMLTableRowElement | null) => registerRowElement(parentId, element)}
        aria-expanded={canExpand ? isExpanded : undefined}
        // role="treegrid" obliges each row to state its depth and position. Parent rows are all
        // level 1; the panel row below carries no aria-level of its own (see the comment there),
        // so it never perturbs this numbering.
        aria-level={1}
        // Both or neither: -1 setsize with a page-local posinset would still announce "item 1"
        // for what is item 21, trading a wrong total for a wrong position.
        aria-posinset={isServerPaginated ? undefined : rowIndex + 1}
        aria-setsize={isServerPaginated ? undefined : rowCount}
        tabIndex={isActiveRow ? 0 : -1}
        onFocus={() => onRowFocus(parentId)}
        onBlur={onRowBlur}
        onKeyDown={(event) => onRowKeyDown(event, row)}
      >
        {row.getVisibleCells().map((cell) => (
          <DimeTableCell key={cell.id} className={cellClassName(cell.column.id)}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </DimeTableCell>
        ))}
      </DimeTableRow>
      {canExpand && isExpanded && (
        <DimeCollapsible open={isExpanded} asChild>
          {/* Deliberately NOT role="presentation": that would also strip the accessible role
              from the cell below it (ARIA's presentational-roles conflict resolution cascades
              from a row to its required-owned cell), which would then promote whatever
              renderChildRows returns -- commonly a whole nested DimeDataTable -- up to become a
              direct, illegal child of the treegrid itself (aria-required-children, confirmed via
              axe). Keeping this a real row+cell gives arbitrary child content a legal home. It
              carries no aria-level/posinset/setsize -- it isn't part of the parent hierarchy
              those describe, so leaving them off doesn't misstate anything. */}
          <DimeTableRow className="hover:bg-transparent has-aria-expanded:bg-transparent">
            <DimeTableCell colSpan={visibleColumnCount} className="p-0">
              <DimeCollapsibleContent className="p-4">
                {renderChildRows({
                  parent: row.original as TParent,
                  children: readCachedChildren(childRowsByParentId, parentId),
                  isLoading: readOwnFlag(loadingParentIds, parentId),
                  parentColumnCount: visibleColumnCount,
                })}
              </DimeCollapsibleContent>
            </DimeTableCell>
          </DimeTableRow>
        </DimeCollapsible>
      )}
    </>
  );
}
